import {useEffect, useState} from "react";
import JSONParser from '@core/scripts/helper/json-parser';

const HISTORY_KEY = 'searchHistory';
const MAX_HISTORY = 10;

/**
 * Storage can be unavailable or full (private mode, blocked site data, quota), and merely
 * touching it can throw. Every access is guarded so that recording a search can never
 * abort the navigation that triggered it.
 */
const readStored = () => {
    try {
        return localStorage.getItem(HISTORY_KEY);
    } catch {
        return null;
    }
};

const writeStored = (history) => {
    try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch {
        // storage unavailable or full — the search itself must still happen
    }
};

const forgetStored = () => {
    try {
        localStorage.removeItem(HISTORY_KEY);
    } catch {
        // storage unavailable
    }
};

/**
 * The history key is user-writable, so anything that is not an array of strings is
 * discarded rather than trusted. A corrupt key is dropped so the store self-heals.
 */
const readSearchHistory = () => {
    const stored = readStored();
    if (!stored) {
        return [];
    }
    const parsed = JSONParser.tryParseJSON(stored);
    if (!Array.isArray(parsed)) {
        forgetStored();
        return [];
    }
    return parsed.filter((term) => typeof term === 'string');
};

const mostRecentFirst = (history) => [...history].reverse().slice(0, MAX_HISTORY);

export function useSearchHistory() {
    const [recentSearches, setRecentSearches] = useState([]);

    useEffect(() => {
        setRecentSearches(mostRecentFirst(readSearchHistory()));
    }, []);

    const addToSearchHistory = (searchValue) => {
        const trimmed = typeof searchValue === 'string' ? searchValue.trim() : '';
        if (!trimmed) {
            return;
        }

        // Deduplicate case-insensitively, so "kleid" and "Kleid" cannot become two chips
        // that render identically once display casing is applied. Newest entry last.
        const kept = readSearchHistory().filter((item) => item.toLowerCase() !== trimmed.toLowerCase());
        kept.push(trimmed);

        // Cap on write too, otherwise the store grows without bound and eventually hits quota.
        const history = kept.slice(-MAX_HISTORY);
        writeStored(history);
        setRecentSearches(mostRecentFirst(history));
    };

    const clearSearchHistory = () => {
        forgetStored();
        setRecentSearches([]);
    };

    return { recentSearches, addToSearchHistory, clearSearchHistory };
}
